// Metric derivation — the port of api/domain/assessment/config/metrics/*. Turns
// a normalised screener input into the boolean/number facts the rules read.
// Kept pure and deterministic (age arrives as a number, not a date) so the same
// function drives Walk it and the scenario enumeration identically.

import type {
  Fact,
  Facts,
  Nation,
  Subject,
} from '@/benefits-flow/engine/types';

// Exact metric key strings, transcribed from the Go source. Rules reference
// these; do not rename without changing rules.ts in lockstep.
export const M = {
  // Location (care_recipient_location.go)
  ukResident: 'care-recipient-uk-resident',
  englandResident: 'care-recipient-england-resident',
  walesResident: 'care-recipient-wales-resident',
  scotlandResident: 'care-recipient-scotland-resident',
  northernIrelandResident: 'care-recipient-northern-ireland-resident',
  // Age (care_recipient_age.go) — reached age is inclusive; under is strict.
  overStatePensionAge: 'care-recipient-over-state-pension-age',
  underStatePensionAge: 'care-recipient-under-state-pension-age',
  over18: 'care-recipient-over-18',
  over60: 'care-recipient-over-60',
  partnerOverStatePensionAge: 'care-recipient-partner-over-state-pension-age',
  // Partner / household
  hasPartner: 'care-recipient-has-partner',
  isSingle: 'care-recipient-is-single',
  // Financial (care_recipient_financial.go) — all strict less-than.
  savings: 'care-recipient-savings',
  savingsBelow16k: 'care-recipient-savings-below-16k',
  savingsBelow23250: 'care-recipient-savings-below-23250',
  weeklyIncomeBelow238: 'care-recipient-weekly-income-below-238',
  jointWeeklyIncomeBelow364: 'care-recipient-joint-weekly-income-below-364',
  weeklyIncomeBelow300: 'care-recipient-weekly-income-below-300',
  jointWeeklyIncomeBelow450: 'care-recipient-joint-weekly-income-below-450',
  // Health (care_recipient_health.go)
  isDisabled: 'care-recipient-is-disabled',
  terminallyIll: 'care-recipient-terminally-ill',
  needsHelpDay: 'care-recipient-needs-help-day',
  needsHelpNight: 'care-recipient-needs-help-night',
  inNursingHome: 'care-recipient-in-nursing-home',
  needsNursingCare: 'care-recipient-needs-nursing-care',
  needsPersonalCare: 'care-recipient-needs-personal-care',
  conditionDuration6Months: 'care-recipient-condition-duration-6-months',
  conditionDuration12MonthsExpected:
    'care-recipient-condition-duration-12-months-expected',
  homeRequiresAdaptations: 'care-recipient-home-requires-adaptations',
  // Disability-type flags used by the bus-pass configs
  isBlindOrPartiallySighted: 'care-recipient-is-blind-or-partially-sighted',
  isDeaf: 'care-recipient-is-deaf',
  withoutSpeech: 'care-recipient-without-speech',
  hasLearningDisability: 'care-recipient-has-learning-disability',
  hasWalkingDisability: 'care-recipient-has-walking-disability',
  hasNoArms: 'care-recipient-has-no-arms-or-loss-of-use-of-arms',
  // Prototype-only, subject = carer (proto-* questions, brief §6.4)
  carerPresent: 'carer-present',
  carerOver16: 'carer-over-16',
  carerFullTimeEducation: 'carer-in-full-time-education',
  carerHours35Plus: 'carer-hours-35-plus',
  carerEarningsBelow204: 'carer-earnings-below-204',
  // Prototype-only, subject = care-recipient: awards the recipient already holds
  // (proto-existing-awards) or has had simulated as awarded. Production never
  // asks what someone already receives — the gap this closes (brief §6.4).
  holdsAttendanceAllowance: 'care-recipient-holds-attendance-allowance',
  holdsPipDailyLiving: 'care-recipient-holds-pip-daily-living',
  holdsPipMobility: 'care-recipient-holds-pip-mobility',
  holdsDlaMiddleOrHighCare: 'care-recipient-holds-dla-middle-or-high-care',
  holdsDlaHigherRateMobility: 'care-recipient-holds-dla-higher-rate-mobility',
  holdsAdultDisabilityPayment: 'care-recipient-holds-adult-disability-payment',
  holdsPensionAgeDisabilityPayment:
    'care-recipient-holds-pension-age-disability-payment',
} as const;

// State pension age and the two other age constants (care_recipient_age.go).
export const STATE_PENSION_AGE = 66;
export const AGE_18 = 18;
export const AGE_60 = 60;
// Carer's Allowance needs 16+; there is deliberately no Age16 in production
// (PIP/ADP have no lower bound — brief §7.3, confirmed). Prototype-only.
export const AGE_16 = 16;

export type DisabilityType =
  | 'blind-or-partially-sighted'
  | 'deaf'
  | 'without-speech'
  | 'learning-disability'
  | 'walking-disability'
  | 'no-arms-or-loss-of-use'
  | 'terminally-ill'
  | 'other';

export type ExistingAward =
  | 'attendance-allowance'
  | 'pip-daily-living'
  | 'pip-mobility'
  | 'dla-middle-or-high-care'
  | 'dla-higher-rate-mobility'
  | 'adult-disability-payment'
  | 'pension-age-disability-payment';

// A benefit whose part or rate decides what it unlocks, so the screener asks
// which before it counts as an award.
export type AwardWithParts = 'pip' | 'dla' | 'adp';

export type ConditionDuration = '6-months' | '12-months-expected' | 'neither';
export type NeedsHelp = 'none' | 'day' | 'night' | 'both';

// The normalised input. Every field is optional — an unanswered field produces
// no fact, which is what drives the three-state "more info needed" outcome.
export type ScreenerInput = {
  nation?: Nation;
  ageYears?: number;
  hasPartner?: boolean;
  partnerAgeYears?: number;
  weeklyIncome?: number;
  jointWeeklyIncome?: number;
  savings?: number;
  isDisabled?: boolean;
  disabilityTypes?: DisabilityType[];
  conditionDuration?: ConditionDuration;
  needsHelp?: NeedsHelp;
  homeRequiresAdaptations?: boolean;
  inNursingHome?: boolean;
  needsPersonalCare?: boolean;
  needsNursingCare?: boolean;
  // proto-* additions
  existingAwards?: ExistingAward[];
  awardsWithParts?: AwardWithParts[];
  carerPresent?: boolean;
  carerAgeYears?: number;
  carerHours35Plus?: boolean;
  carerEarningsBelow204?: boolean;
  carerFullTimeEducation?: boolean;
};

const f = (key: string, value: boolean | number, subject: Subject): Fact => ({
  key,
  value,
  subject,
});

const CR: Subject = 'care-recipient';

// Map a disability-type selection onto the metric flags the bus-pass and
// terminal-illness rules read. Screener option ids differ slightly from metric
// keys (e.g. `deaf` → `is-deaf`), so the mapping is explicit.
const DISABILITY_METRIC: Record<DisabilityType, string | null> = {
  'blind-or-partially-sighted': M.isBlindOrPartiallySighted,
  deaf: M.isDeaf,
  'without-speech': M.withoutSpeech,
  'learning-disability': M.hasLearningDisability,
  'walking-disability': M.hasWalkingDisability,
  'no-arms-or-loss-of-use': M.hasNoArms,
  'terminally-ill': M.terminallyIll,
  other: null,
};

const AWARD_METRIC: Record<ExistingAward, string> = {
  'attendance-allowance': M.holdsAttendanceAllowance,
  'pip-daily-living': M.holdsPipDailyLiving,
  'pip-mobility': M.holdsPipMobility,
  'dla-middle-or-high-care': M.holdsDlaMiddleOrHighCare,
  'dla-higher-rate-mobility': M.holdsDlaHigherRateMobility,
  'adult-disability-payment': M.holdsAdultDisabilityPayment,
  'pension-age-disability-payment': M.holdsPensionAgeDisabilityPayment,
};

// Derive the full facts map. A fact is only added when the underlying question
// was answered — mirroring production, where an unrun calculator writes nothing.
export const deriveFacts = (input: ScreenerInput): Facts => {
  const facts: Facts = {};
  const add = (fact: Fact) => {
    facts[fact.key] = fact;
  };

  // Location — once nation is known, every nation flag is present (true or
  // false). `other` leaves all five false, exactly as an unmatched country does
  // in production. This is also why an NI resident is caught by the Council Tax
  // Reduction NI bug: the england/wales/scotland flags are present-and-false.
  if (input.nation !== undefined) {
    const n = input.nation;
    const isUk: Record<Nation, boolean> = {
      england: true,
      scotland: true,
      wales: true,
      'northern-ireland': true,
      other: false,
    };
    add(f(M.ukResident, isUk[n], CR));
    add(f(M.englandResident, n === 'england', CR));
    add(f(M.walesResident, n === 'wales', CR));
    add(f(M.scotlandResident, n === 'scotland', CR));
    add(f(M.northernIrelandResident, n === 'northern-ireland', CR));
  }

  // Age (care recipient). Reached-age metrics are inclusive (age ≥ n);
  // under-pension-age is strict (age < 66).
  if (input.ageYears !== undefined) {
    const a = input.ageYears;
    add(f(M.overStatePensionAge, a >= STATE_PENSION_AGE, CR));
    add(f(M.underStatePensionAge, a < STATE_PENSION_AGE, CR));
    add(f(M.over18, a >= AGE_18, CR));
    add(f(M.over60, a >= AGE_60, CR));
  }

  // Partner / household
  if (input.hasPartner !== undefined) {
    add(f(M.hasPartner, input.hasPartner, CR));
    add(f(M.isSingle, !input.hasPartner, CR));
  }
  if (input.hasPartner === true && input.partnerAgeYears !== undefined) {
    add(
      f(
        M.partnerOverStatePensionAge,
        input.partnerAgeYears >= STATE_PENSION_AGE,
        'partner',
      ),
    );
  }

  // Income — single vs joint, mirroring the two production questions gated on
  // has-partner. Thresholds are strict less-than.
  if (input.hasPartner === false && input.weeklyIncome !== undefined) {
    add(f(M.weeklyIncomeBelow238, input.weeklyIncome < 238, CR));
    add(f(M.weeklyIncomeBelow300, input.weeklyIncome < 300, CR));
  }
  if (input.hasPartner === true && input.jointWeeklyIncome !== undefined) {
    add(
      f(
        M.jointWeeklyIncomeBelow364,
        input.jointWeeklyIncome < 364,
        'household',
      ),
    );
    add(
      f(
        M.jointWeeklyIncomeBelow450,
        input.jointWeeklyIncome < 450,
        'household',
      ),
    );
  }

  // Savings
  if (input.savings !== undefined) {
    add(f(M.savings, input.savings, CR));
    add(f(M.savingsBelow16k, input.savings < 16000, CR));
    add(f(M.savingsBelow23250, input.savings < 23250, CR));
  }

  // Disability block — all gated on is-disabled in production.
  if (input.isDisabled !== undefined) {
    add(f(M.isDisabled, input.isDisabled, CR));
  }
  if (input.isDisabled === true && input.disabilityTypes !== undefined) {
    const types = input.disabilityTypes;
    // Each flag is present (true/false) once disability types are answered.
    for (const [type, metric] of Object.entries(DISABILITY_METRIC)) {
      if (metric === null) continue;
      add(f(metric, types.includes(type as DisabilityType), CR));
    }
  }
  // Terminal illness is asked of everyone, so somebody who said no to the
  // disability question still gets the fact the terminal rules read.
  if (input.isDisabled !== true && input.disabilityTypes !== undefined) {
    add(
      f(M.terminallyIll, input.disabilityTypes.includes('terminally-ill'), CR),
    );
  }
  if (input.isDisabled === true && input.conditionDuration !== undefined) {
    const d = input.conditionDuration;
    add(f(M.conditionDuration6Months, d === '6-months', CR));
    add(f(M.conditionDuration12MonthsExpected, d === '12-months-expected', CR));
  }
  // Needs-help is NOT gated on is-disabled here (production asks it inside the
  // disability block, but the reordered screener surfaces it at question three,
  // before is-disabled, to fire the Attendance Allowance reveal early). Derived
  // whenever answered.
  if (input.needsHelp !== undefined) {
    const h = input.needsHelp;
    add(f(M.needsHelpDay, h === 'day' || h === 'both', CR));
    add(f(M.needsHelpNight, h === 'night' || h === 'both', CR));
  }
  if (
    input.isDisabled === true &&
    input.homeRequiresAdaptations !== undefined
  ) {
    add(f(M.homeRequiresAdaptations, input.homeRequiresAdaptations, CR));
  }

  // Care-setting questions — nation-gated in the screener but the metric is
  // simply present when answered.
  if (input.inNursingHome !== undefined) {
    add(f(M.inNursingHome, input.inNursingHome, CR));
  }
  if (input.needsPersonalCare !== undefined) {
    add(f(M.needsPersonalCare, input.needsPersonalCare, CR));
  }
  if (input.needsNursingCare !== undefined) {
    add(f(M.needsNursingCare, input.needsNursingCare, CR));
  }

  // proto-* existing awards (subject: care recipient)
  if (input.existingAwards !== undefined) {
    for (const [award, metric] of Object.entries(AWARD_METRIC)) {
      add(f(metric, input.existingAwards.includes(award as ExistingAward), CR));
    }
  }

  // proto-* carer block (subject: carer). `carer-present` is present the moment
  // the question is answered, so "no carer" is a definite ineligible for
  // Carer's Allowance rather than an unknown. The rest is gated on a carer
  // actually being there.
  if (input.carerPresent !== undefined) {
    add(f(M.carerPresent, input.carerPresent, 'carer'));
  }
  if (input.carerPresent === true) {
    if (input.carerAgeYears !== undefined) {
      add(f(M.carerOver16, input.carerAgeYears >= AGE_16, 'carer'));
    }
    if (input.carerFullTimeEducation !== undefined) {
      add(f(M.carerFullTimeEducation, input.carerFullTimeEducation, 'carer'));
    }
    if (input.carerHours35Plus !== undefined) {
      add(f(M.carerHours35Plus, input.carerHours35Plus, 'carer'));
    }
    if (input.carerEarningsBelow204 !== undefined) {
      add(f(M.carerEarningsBelow204, input.carerEarningsBelow204, 'carer'));
    }
  }

  return facts;
};
